import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Calendar, Users, Clock, Check, UserPlus, ChevronRight, Flag } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Separate component to use swipeable hook properly
const ClassCard = ({ 
  classSession, booked, bookingDetails, isFull, capacityPercent, isCheckedIn, 
  bookingClosed,
  swipedCard, onSwipeLeft, onSwipeRight, onBook, onCancel, onCheckIn, 
  onShowAttendees, showingAttendees, attendees 
}) => {
  const handlers = useSwipeable({
    onSwipedLeft: onSwipeLeft,
    onSwipedRight: onSwipeRight,
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  return (
    <div {...handlers}>
      <Card
        className={`bg-[#18181B] border-[#27272A] p-5 transition-all ${
          booked ? 'border-[#CCFF00]/30 shadow-[0_0_20px_rgba(204,255,0,0.1)]' : ''
        } ${
          swipedCard === classSession.id ? 'scale-95 opacity-70' : ''
        }`}
        data-testid={`class-card-${classSession.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-heading font-bold uppercase text-[#FAFAFA]">
            {classSession.title}
          </h3>
          {booked && (
            <div className="flex flex-col gap-1 items-end">
              {isCheckedIn ? (
                <div className="flex items-center gap-1.5 bg-[#22C55E]/10 border border-[#22C55E]/30 px-2 py-1 rounded-sm">
                  <img
                    src="https://customer-assets.emergentagent.com/job_wod-trainer/artifacts/rhui1jxy_R%26R%20ELITE_20250812_172035_0000.png"
                    alt="Attended"
                    className="w-5 h-5 object-contain"
                  />
                  <span className="text-[#22C55E] text-xs font-bold uppercase">Attended</span>
                </div>
              ) : (
                <span className="bg-[#CCFF00] text-black text-xs font-bold px-2 py-1 rounded-sm uppercase">
                  Booked
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 text-[#A1A1AA] mb-4">
          <Clock className="w-4 h-4" />
          <span className="text-sm font-semibold">
            {classSession.start_time} - {classSession.end_time}
          </span>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-[#A1A1AA] uppercase tracking-wide">Capacity</span>
            <span className="text-sm font-bold text-[#FAFAFA]">
              {classSession.booked_count} / {classSession.max_capacity}
            </span>
          </div>
          <Progress value={capacityPercent} className="h-2" data-testid={`capacity-progress-${classSession.id}`} />
        </div>

        <div className="space-y-2">
          {!booked ? (
            <>
              {bookingClosed ? (
                <div
                  className="w-full py-2.5 text-center text-xs font-bold uppercase tracking-wider text-[#52525B] bg-[#09090B] border border-[#27272A] rounded-sm"
                  data-testid={`booking-closed-${classSession.id}`}
                >
                  Bookings Closed
                </div>
              ) : (
                <>
                  <Button
                    onClick={onBook}
                    disabled={isFull}
                    data-testid={`book-button-${classSession.id}`}
                    className={`w-full font-bold uppercase tracking-wider rounded-sm ${
                      isFull
                        ? 'bg-[#27272A] text-[#52525B] cursor-not-allowed'
                        : 'bg-[#CCFF00] text-black hover:bg-[#B3E600] shadow-[0_0_15px_rgba(204,255,0,0.2)] active:scale-95'
                    }`}
                  >
                    {isFull ? 'Class Full' : 'Book Now'}
                  </Button>
                  {!isFull && (
                    <p className="text-xs text-[#A1A1AA] text-center md:hidden">
                      Swipe right to book quickly →
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {isCheckedIn ? (
                <p className="text-xs text-[#22C55E] text-center font-semibold uppercase tracking-wide py-2">
                  You're checked in for this class
                </p>
              ) : (
                <>
                  <p className="text-xs text-[#A1A1AA] text-center py-1">
                    Scan the QR code at the gym to check in
                  </p>
                  <Button
                    onClick={onCancel}
                    data-testid={`cancel-button-${classSession.id}`}
                    variant="outline"
                    className="w-full border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#EF4444] uppercase tracking-wider rounded-sm"
                  >
                    Cancel Booking
                  </Button>
                  {!bookingClosed && (
                    <p className="text-xs text-[#A1A1AA] text-center md:hidden">
                      ← Swipe left to cancel
                    </p>
                  )}
                </>
              )}
            </>
          )}
          <Button
            onClick={onShowAttendees}
            data-testid={`view-attendees-${classSession.id}`}
            variant="outline"
            size="sm"
            className="w-full text-xs uppercase tracking-wide border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]"
          >
            <Users className="w-3 h-3 mr-2" />
            {showingAttendees ? 'Hide' : 'View'} Who's Coming
          </Button>
        </div>

        {showingAttendees && attendees && (
          <div className="mt-4 p-3 bg-[#09090B] rounded-sm border border-[#27272A]" data-testid={`attendees-list-${classSession.id}`}>
            {attendees.length === 0 ? (
              <p className="text-[#A1A1AA] text-xs">No one booked yet</p>
            ) : (
              <ul className="space-y-1">
                {attendees.map((attendee) => (
                  <li key={attendee.booking_id || attendee.id} className="text-[#FAFAFA] text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#CCFF00] rounded-full"></span>
                    {attendee.user ? attendee.user.name : attendee.name}
                    {attendee.guest_count > 0 && (
                      <span className="text-xs text-[#A1A1AA]">+ {attendee.guest_count} guest{attendee.guest_count > 1 ? 's' : ''}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default function Schedule({ user }) {
  const [classes, setClasses] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [attendees, setAttendees] = useState({});
  const [showingAttendees, setShowingAttendees] = useState(null);
  const [swipedCard, setSwipedCard] = useState(null);
  
  // Guest booking dialog
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [guestCount, setGuestCount] = useState(0);

  useEffect(() => {
    fetchData();
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const year = new Date().getFullYear();
      const [r1, r2] = await Promise.all([
        fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/ZA`),
        fetch(`https://date.nager.at/api/v3/PublicHolidays/${year + 1}/ZA`)
      ]);
      const [h1, h2] = await Promise.all([r1.json(), r2.json()]);
      const map = {};
      [...h1, ...h2].forEach(h => { map[h.date] = h.localName; });
      setHolidays(map);
    } catch {
      // Silently fail
    }
  };

  const fetchData = async () => {
    try {
      const [classesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/classes`),
        axios.get(`${API}/bookings/my-bookings`)
      ]);
      setClasses(classesRes.data);
      setMyBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const openBookingDialog = (classSession) => {
    setSelectedClass(classSession);
    setGuestCount(0);
    setBookingDialogOpen(true);
  };

  const bookClass = async () => {
    if (!selectedClass) return;
    
    try {
      await axios.post(`${API}/bookings`, { 
        class_id: selectedClass.id,
        guest_count: guestCount
      });
      toast.success(`Class booked successfully!${guestCount > 0 ? ` (+ ${guestCount} guest${guestCount > 1 ? 's' : ''})` : ''}`);
      setBookingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book class');
    }
  };

  const confirmCancelBooking = (bookingId) => {
    setSelectedBooking(bookingId);
    setCancelDialogOpen(true);
  };

  const cancelBooking = async () => {
    try {
      await axios.delete(`${API}/bookings/${selectedBooking}`);
      toast.success('Booking cancelled');
      setCancelDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const checkIn = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/check-in`);
      toast.success('Checked in successfully! ✓');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in');
    }
  };

  const fetchClassAttendees = async (classId) => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/bookings`);
      return response.data;
    } catch (error) {
      toast.error('Failed to load attendees');
      return [];
    }
  };

  const showAttendees = async (classId) => {
    if (showingAttendees === classId) {
      setShowingAttendees(null);
      return;
    }
    const users = await fetchClassAttendees(classId);
    setAttendees({ ...attendees, [classId]: users });
    setShowingAttendees(classId);
  };

  const isBooked = (classId) => {
    return myBookings.some(b => b.id === classId);
  };

  const getBookingId = (classId) => {
    const booking = myBookings.find(b => b.id === classId);
    return booking?.booking_id;
  };

  const getBookingDetails = (classId) => {
    return myBookings.find(b => b.id === classId);
  };

  const upcomingClasses = classes.filter(c => {
    const classDate = new Date(c.date);
    const today = new Date();
    
    // If class is on a future date, include it
    if (classDate.toDateString() !== today.toDateString()) {
      classDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return classDate >= today;
    }
    
    // If class is today, check if end time has passed
    const [endHours, endMinutes] = c.end_time.split(':').map(Number);
    const classEndTime = new Date();
    classEndTime.setHours(endHours, endMinutes, 0, 0);
    
    return new Date() < classEndTime;
  });

  const groupedClasses = upcomingClasses.reduce((acc, classSession) => {
    if (!acc[classSession.date]) {
      acc[classSession.date] = [];
    }
    acc[classSession.date].push(classSession);
    return acc;
  }, {});

  // Sort dates chronologically
  const sortedDates = Object.keys(groupedClasses).sort((a, b) => {
    return new Date(a) - new Date(b);
  });
  
  // Sort classes within each date by start time (morning to evening)
  sortedDates.forEach(date => {
    groupedClasses[date].sort((a, b) => {
      return a.start_time.localeCompare(b.start_time);
    });
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="schedule-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2">
            Class Schedule
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">Book your spot in upcoming classes</p>
        </div>

        {sortedDates.length === 0 ? (
          <Card className="bg-[#18181B] border-[#27272A] p-8" data-testid="no-classes-message">
            <p className="text-[#A1A1AA] text-center text-lg">No upcoming classes scheduled.</p>
          </Card>
        ) : (
          <div className="space-y-12">
            {sortedDates.map((date) => (
              <div key={date} data-testid={`date-group-${date}`}>
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                  <Calendar className="w-6 h-6 text-[#CCFF00]" />
                  <h2 className="text-2xl font-heading font-bold uppercase text-[#FAFAFA]">
                    {new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h2>
                  {holidays[date] && (
                    <span
                      data-testid={`holiday-badge-${date}`}
                      className="flex items-center gap-1.5 bg-[#38BDF8] text-black text-xs font-bold px-2.5 py-1 rounded-sm uppercase tracking-wide"
                    >
                      <Flag className="w-3 h-3" />
                      {holidays[date]}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedClasses[date].map((classSession) => {
                      const booked = isBooked(classSession.id);
                      const bookingDetails = getBookingDetails(classSession.id);
                      const isFull = classSession.booked_count >= classSession.max_capacity;
                      const capacityPercent = (classSession.booked_count / classSession.max_capacity) * 100;
                      const isCheckedIn = bookingDetails?.checked_in || false;
                      // 45-min cutoff: compare class start (SAST = UTC+2) with now
                      // Skip cutoff if coach has enabled late booking override
                      const classStart = new Date(`${classSession.date}T${classSession.start_time}:00+02:00`);
                      const bookingClosed = !classSession.late_booking_override &&
                        (classStart - new Date()) < 45 * 60 * 1000;

                      return (
                        <ClassCard
                          key={classSession.id}
                          classSession={classSession}
                          booked={booked}
                          bookingDetails={bookingDetails}
                          isFull={isFull}
                          capacityPercent={capacityPercent}
                          isCheckedIn={isCheckedIn}
                          bookingClosed={bookingClosed}
                          swipedCard={swipedCard}
                          onSwipeLeft={() => {
                            if (booked) {
                              setSwipedCard(classSession.id);
                              setTimeout(() => setSwipedCard(null), 300);
                              confirmCancelBooking(getBookingId(classSession.id));
                            }
                          }}
                          onSwipeRight={() => {
                            if (!booked && !isFull && !bookingClosed) {
                              setSwipedCard(classSession.id);
                              setTimeout(() => setSwipedCard(null), 300);
                              openBookingDialog(classSession);
                            }
                          }}
                          onBook={() => openBookingDialog(classSession)}
                          onCancel={() => confirmCancelBooking(getBookingId(classSession.id))}
                          onCheckIn={() => checkIn(getBookingId(classSession.id))}
                          onShowAttendees={() => showAttendees(classSession.id)}
                          showingAttendees={showingAttendees === classSession.id}
                          attendees={attendees[classSession.id]}
                        />
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Booking Dialog with Guest Option */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-black uppercase text-[#FAFAFA]">
              Book Class
            </DialogTitle>
            {selectedClass && (
              <DialogDescription className="text-[#A1A1AA]">
                {selectedClass.title} on {new Date(selectedClass.date).toLocaleDateString()}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="guest-count" className="text-[#FAFAFA] uppercase text-xs font-semibold flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Bringing Guests?
              </Label>
              <Input
                id="guest-count"
                data-testid="guest-count-input"
                type="number"
                min="0"
                max="5"
                value={guestCount}
                onChange={(e) => setGuestCount(parseInt(e.target.value) || 0)}
                className="bg-[#09090B] border-[#27272A] text-white focus:border-[#CCFF00] h-12"
                placeholder="0"
              />
              <p className="text-xs text-[#A1A1AA]">
                How many guests will join you? (Maximum 5)
              </p>
            </div>
            {guestCount > 0 && (
              <div className="p-3 bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded-sm">
                <p className="text-sm text-[#FAFAFA]">
                  Total attendees: <span className="font-bold">1 (you) + {guestCount} guest{guestCount > 1 ? 's' : ''}</span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setBookingDialogOpen(false)}
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
            >
              Cancel
            </Button>
            <Button
              onClick={bookClass}
              data-testid="confirm-booking-button"
              className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600]"
            >
              Book Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A]" data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#FAFAFA] font-heading uppercase">Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure you want to cancel this booking?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]" data-testid="cancel-dialog-cancel">
              Keep Booking
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={cancelBooking}
              data-testid="cancel-dialog-confirm"
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
